import { ElementType } from "react";

import {
    Backdrop,
    Box,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    styled,
    Typography,
} from "@mui/material";

/** Keeps `$`-prefixed styling props from reaching MUI components / the DOM. */
const transientOptions = {
    shouldForwardProp: (prop: PropertyKey) => !String(prop).startsWith("$"),
};

export interface StyledPaperProps {
    /**
     * Paper height. Defaults to a responsive fixed height (see
     * `MODAL_HEIGHT`) so steps/content changes don't resize the dialog;
     * pass `"auto"` to size the dialog to its content instead.
     */
    $fixedHeight?: string | number;
    $minHeight?: string | number;
}

/** Default dialog height per viewport size; mobile is full screen anyway. */
export const MODAL_HEIGHT = {
    desktop: "90vh",
    tablet: "94vh",
    auto: "auto",
} as const;

/** Space the dialog keeps from the viewport edges. */
const PAPER_MARGIN = 24;
const PAPER_MAX_HEIGHT = `calc(100% - ${PAPER_MARGIN * 2}px)`;

const WIDTH_KEYS = ["xs", "sm", "md", "lg", "xl"] as const;

/**
 * Replaces MUI's `DialogPaper` slot, so it re-implements that slot's layout
 * rules (margin, `maxWidth`, `fullWidth`, `fullScreen`) on top of the
 * `MuiDialog-paper*` classes the Dialog still applies.
 */
export const StyledPaper = styled(
    Paper,
    transientOptions,
)<StyledPaperProps>(({ theme, $fixedHeight, $minHeight }) => ({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    margin: PAPER_MARGIN,
    maxHeight: PAPER_MAX_HEIGHT,
    borderRadius: 16,
    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
    overflow: "hidden",

    ...(!$fixedHeight && {
        height: MODAL_HEIGHT.desktop,
        [theme.breakpoints.down("md")]: { height: MODAL_HEIGHT.tablet },
    }),
    ...($fixedHeight &&
        $fixedHeight !== MODAL_HEIGHT.auto && { height: $fixedHeight }),
    ...($minHeight && { minHeight: $minHeight }),

    ...Object.fromEntries(
        WIDTH_KEYS.map((key) => [
            `&.MuiDialog-paperWidth${key.charAt(0).toUpperCase()}${key.slice(1)}`,
            {
                maxWidth:
                    key === "xs"
                        ? Math.max(theme.breakpoints.values.xs, 444)
                        : theme.breakpoints.values[key],
            },
        ]),
    ),
    "&.MuiDialog-paperFullWidth": {
        width: `calc(100% - ${PAPER_MARGIN * 2}px)`,
    },
    "&.MuiDialog-paperFullScreen": {
        margin: 0,
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        maxHeight: "none",
        borderRadius: 0,
    },
}));

/** Replaces MUI's `DialogBackdrop`, which sits behind the paper via `z-index: -1`. */
export const StyledBackdrop = styled(Backdrop)({
    zIndex: -1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(2px)",
});

export const Header = styled(
    DialogTitle,
    transientOptions,
)<{ $backgroundColor?: string; component?: ElementType }>(
    ({ theme, $backgroundColor }) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: $backgroundColor || theme.palette.background.paper,
        position: "relative",
        flexShrink: 0,
    }),
);

export const HeaderTopRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
}));

/**
 * `[title | center | actions]` row. With `$withCenter` it becomes a 3-column
 * grid whose outer columns share width equally, so the center slot (e.g. a
 * step indicator) is truly centered regardless of title/actions width.
 */
export const TitleRow = styled(
    Box,
    transientOptions,
)<{ $withCenter?: boolean }>(({ theme, $withCenter }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
        gap: theme.spacing(2),
    },
    ...($withCenter && {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    }),
}));

export const HeaderCenter = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
});

export const TitleGroup = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.up("sm")]: {
        gap: theme.spacing(2),
    },
}));

export const IconWrap = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    flexShrink: 0,
    color: theme.palette.primary.contrastText,
}));

export const TitleText = styled(Box)({
    minWidth: 0,
});

export const Title = styled(Typography)<{ component?: ElementType }>(
    ({ theme }) => ({
        fontWeight: 600,
        color: theme.palette.text.primary,
        lineHeight: 1.2,
    }),
);

export const Subtitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    marginTop: theme.spacing(0.25),
    wordBreak: "break-word",
}));

export const Actions = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
    justifySelf: "end",
    [theme.breakpoints.up("sm")]: {
        gap: theme.spacing(1),
    },
}));

export const ActionsGroup = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    [theme.breakpoints.up("sm")]: {
        gap: theme.spacing(1),
    },
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
    },
}));

export const Content = styled(DialogContent)({
    padding: 0,
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "auto",
    scrollbarGutter: "stable",
    overscrollBehavior: "contain",
});

export const Footer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
}));
